import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2,
  Send,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  DollarSign,
  ChevronRight,
} from "lucide-react";
import { format, formatDistanceToNow, subDays } from "date-fns";
import { cn } from "@/lib/utils";

// Types

interface CampaignEvent {
  event_id: string;
  contact_id: string;
  event_type: string;
  occurred_at: string;
  payload: Record<string, unknown>;
}

type Outcome = "sent" | "responded" | "converted" | "ignored";

interface RecipientRow {
  contact_id: string;
  sent_at: string;
  channel: string | null;
  outcome: Outcome;
  outcome_at: string | null;
  outcome_type: string | null;
  outcome_event_id: string | null;
  decision_trace: any | null;
}

interface CampaignAgg {
  campaign_id: string;
  first_sent_at: string;
  trigger_source: "cron_segment" | "manual_admin" | "unknown";
  recipients: RecipientRow[];
  sent_count: number;
  responded_count: number;
  converted_count: number;
  ignored_count: number;
}

const safeDistance = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return formatDistanceToNow(d, { addSuffix: true });
};

const formatCampaignLabel = (id: string) => {
  if (id.startsWith("manual_winback")) return "Manual winback";
  if (id.startsWith("dormant_winback:")) return `Dormant winback · ${id.split(":")[1]}`;
  return id;
};

// Main page

const CampaignsAdmin = () => {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignAgg | null>(null);

  // Winback mode setting: review_first (default) or auto_send.
  const { data: winbackMode = "review_first" } = useQuery({
    queryKey: ["winback_mode", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<"review_first" | "auto_send"> => {
      const { data } = await (supabase as any)
        .from("ai_agent_configs")
        .select("winback_mode")
        .eq("business_id", businessId)
        .maybeSingle();
      return (data?.winback_mode === "auto_send" ? "auto_send" : "review_first");
    },
  });

  const [pendingMode, setPendingMode] = useState<"review_first" | "auto_send" | null>(null);
  const handleModeChange = async (next: "review_first" | "auto_send") => {
    setPendingMode(next);
    try {
      const { error } = await (supabase as any)
        .from("ai_agent_configs")
        .update({ winback_mode: next })
        .eq("business_id", businessId);
      if (error) throw error;
      toast.success(
        next === "auto_send"
          ? "Auto-send enabled — daily batches go out without review"
          : "Review-first enabled — daily batches will wait for your approval",
      );
      queryClient.invalidateQueries({ queryKey: ["winback_mode", businessId] });
    } catch (e: any) {
      toast.error(e.message || "Failed to update setting");
    } finally {
      setPendingMode(null);
    }
  };

  // Include-images default: whether winback MESSAGES carry a hero image (email
  // hero + Telegram sendPhoto). Defaults to true (the column default); per-batch
  // and per-manual-send overrides can still flip it for an individual send.
  const { data: includeImages = true } = useQuery({
    queryKey: ["winback_include_images", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<boolean> => {
      const { data } = await (supabase as any)
        .from("ai_agent_configs")
        .select("winback_include_images")
        .eq("business_id", businessId)
        .maybeSingle();
      return data?.winback_include_images !== false;
    },
  });

  const [pendingImages, setPendingImages] = useState(false);
  const handleImagesChange = async (next: boolean) => {
    setPendingImages(true);
    try {
      const { error } = await (supabase as any)
        .from("ai_agent_configs")
        .update({ winback_include_images: next })
        .eq("business_id", businessId);
      if (error) throw error;
      toast.success(
        next
          ? "Winback messages will include a listing photo"
          : "Winback messages will be text-only",
      );
      queryClient.invalidateQueries({ queryKey: ["winback_include_images", businessId] });
    } catch (e: any) {
      toast.error(e.message || "Failed to update setting");
    } finally {
      setPendingImages(false);
    }
  };

  // Pull all campaign events for the business.
  const { data: events, isLoading } = useQuery({
    queryKey: ["campaign_events", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<CampaignEvent[]> => {
      const { data, error } = await (supabase as any)
        .from("contact_events")
        .select("event_id, contact_id, event_type, occurred_at, payload")
        .eq("business_id", businessId)
        .in("event_type", ["CampaignSent", "CampaignResponded", "CampaignConverted", "CampaignIgnored"])
        .order("occurred_at", { ascending: false })
        .limit(1000);
      if (error) {
        if (error.code === "42P01" || error.code === "PGRST205") return [];
        throw error;
      }
      return (data ?? []) as CampaignEvent[];
    },
  });

  // Aggregate per campaign_id.
  const campaigns = useMemo<CampaignAgg[]>(() => {
    if (!events || events.length === 0) return [];
    const map = new Map<string, CampaignAgg>();
    const recipientMap = new Map<string, Map<string, RecipientRow>>();

    for (const e of events) {
      const payload = e.payload ?? {};
      const cid = payload.campaign_id as string | undefined;
      if (!cid) continue;

      let agg = map.get(cid);
      if (!agg) {
        agg = {
          campaign_id: cid,
          first_sent_at: e.occurred_at,
          trigger_source: "unknown",
          recipients: [],
          sent_count: 0,
          responded_count: 0,
          converted_count: 0,
          ignored_count: 0,
        };
        map.set(cid, agg);
        recipientMap.set(cid, new Map());
      }
      const recipients = recipientMap.get(cid)!;
      let rec = recipients.get(e.contact_id);
      if (!rec) {
        rec = {
          contact_id: e.contact_id,
          sent_at: e.occurred_at,
          channel: null,
          outcome: "sent",
          outcome_at: null,
          outcome_type: null,
          outcome_event_id: null,
          decision_trace: null,
        };
        recipients.set(e.contact_id, rec);
      }

      if (e.event_type === "CampaignSent") {
        rec.sent_at = e.occurred_at;
        rec.channel = (payload.channel as string) ?? null;
        rec.decision_trace = (payload.decision_trace as any) ?? null;
        const triggerSource = rec.decision_trace?.trigger_source;
        if (triggerSource && agg.trigger_source === "unknown") {
          agg.trigger_source = triggerSource;
        }
        if (new Date(e.occurred_at) < new Date(agg.first_sent_at)) {
          agg.first_sent_at = e.occurred_at;
        }
      } else if (e.event_type === "CampaignConverted") {
        rec.outcome = "converted";
        rec.outcome_at = e.occurred_at;
        rec.outcome_type = (payload.outcome_type as string) ?? null;
        rec.outcome_event_id = (payload.outcome_event_id as string) ?? null;
      } else if (e.event_type === "CampaignResponded" && rec.outcome !== "converted") {
        rec.outcome = "responded";
        rec.outcome_at = e.occurred_at;
      } else if (e.event_type === "CampaignIgnored" && rec.outcome === "sent") {
        rec.outcome = "ignored";
        rec.outcome_at = e.occurred_at;
      }
    }

    for (const [cid, recipients] of recipientMap) {
      const agg = map.get(cid)!;
      agg.recipients = Array.from(recipients.values()).sort(
        (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
      );
      agg.sent_count = agg.recipients.length;
      agg.responded_count = agg.recipients.filter((r) => r.outcome === "responded" || r.outcome === "converted").length;
      agg.converted_count = agg.recipients.filter((r) => r.outcome === "converted").length;
      agg.ignored_count = agg.recipients.filter((r) => r.outcome === "ignored").length;
    }

    return Array.from(map.values()).sort(
      (a, b) => new Date(b.first_sent_at).getTime() - new Date(a.first_sent_at).getTime(),
    );
  }, [events]);

  const convertedOutcomeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of campaigns) {
      for (const r of c.recipients) {
        if (r.outcome === "converted" && r.outcome_event_id) ids.add(r.outcome_event_id);
      }
    }
    return Array.from(ids);
  }, [campaigns]);

  const { data: attributedBookings } = useQuery({
    queryKey: ["attributed_bookings", convertedOutcomeIds.join(",")],
    enabled: convertedOutcomeIds.length > 0,
    queryFn: async (): Promise<Record<string, { booking_id: string; price: number; name?: string; short_id?: string; created_at?: string }>> => {
      const { data: outcomeEvents } = await (supabase as any)
        .from("contact_events")
        .select("event_id, payload")
        .in("event_id", convertedOutcomeIds);
      const rows = (outcomeEvents ?? []) as Array<{ event_id: string; payload: any }>;
      const bookingIdToEvent = new Map<string, string>();
      for (const r of rows) {
        const bid = r.payload?.booking_id as string | undefined;
        if (bid) bookingIdToEvent.set(bid, r.event_id);
      }
      if (bookingIdToEvent.size === 0) return {};
      const { data: bookings } = await (supabase as any)
        .from("bookings")
        .select("booking_id, short_id, created_at, listings(name, price)")
        .in("booking_id", Array.from(bookingIdToEvent.keys()));
      const out: Record<string, { booking_id: string; price: number; name?: string; short_id?: string; created_at?: string }> = {};
      for (const b of (bookings ?? []) as any[]) {
        const eventId = bookingIdToEvent.get(b.booking_id);
        if (!eventId) continue;
        out[eventId] = {
          booking_id: b.booking_id,
          price: Number(b.listings?.price ?? 0),
          name: b.listings?.name,
          short_id: b.short_id,
          created_at: b.created_at,
        };
      }
      return out;
    },
  });

  const { data: recentBatchHistory } = useQuery({
    queryKey: ["winback_batch_history", businessId],
    enabled: !!businessId && winbackMode === "review_first",
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("action_approval_queue")
        .select("status, reviewed_at")
        .eq("business_id", businessId)
        .eq("action_name", "winback_batch")
        .in("status", ["approved", "rejected"])
        .order("reviewed_at", { ascending: false })
        .limit(5);
      return (data ?? []) as Array<{ status: string; reviewed_at: string }>;
    },
  });

  const eligibleForGraduation = useMemo(() => {
    const rows = recentBatchHistory ?? [];
    if (rows.length < 3) return false;
    return rows.slice(0, 3).every((r) => r.status === "approved");
  }, [recentBatchHistory]);

  // Learning-loop stats (nightly campaignLearningAggregate). Absent on cold
  // start. The insight line simply doesn't render.
  const { data: learningStats } = useQuery({
    queryKey: ["campaign_learning_stats", businessId],
    enabled: !!businessId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("campaign_learning_stats")
        .select("stats, sample_size, computed_at")
        .eq("business_id", businessId)
        .maybeSingle();
      return data ?? null;
    },
  });

  const learningInsight = useMemo(() => {
    const stats = learningStats?.stats;
    if (!stats) return null;
    type Bucket = { sent: number; replied: number; converted: number; ignored: number };
    const best = (buckets: Record<string, Bucket> | undefined): [string, number] | null => {
      const entries = Object.entries(buckets ?? {}).filter(([k]) => k !== "unknown" && k !== "no_offer_recorded");
      if (entries.length === 0) return null;
      const ranked = entries
        .map(([k, b]) => [k, (b.replied + b.converted) / b.sent] as [string, number])
        .sort((a, b) => b[1] - a[1]);
      return ranked[0][1] > 0 ? ranked[0] : null;
    };
    const bits: string[] = [];
    const ch = best(stats.by_channel);
    if (ch) bits.push(`${ch[0]} gets the best engagement (${Math.round(ch[1] * 100)}%)`);
    const band = best(stats.by_send_band);
    if (band) bits.push(`${band[0]} sends land best`);
    const offer = best(stats.by_offer_type);
    if (offer) bits.push(`${offer[0]} offers resonate most`);
    if (bits.length === 0) return null;
    return `What's working: ${bits.join(" · ")} — based on ${learningStats.sample_size} sends over the last ${stats.lookback_days ?? 180} days. New campaigns use this automatically.`;
  }, [learningStats]);

  const kpis = useMemo(() => {
    const cutoff = subDays(new Date(), 30);
    let sent = 0;
    let responded = 0;
    let converted = 0;
    let revenue = 0;
    for (const c of campaigns) {
      for (const r of c.recipients) {
        if (new Date(r.sent_at) < cutoff) continue;
        sent++;
        if (r.outcome === "responded" || r.outcome === "converted") responded++;
        if (r.outcome === "converted") {
          converted++;
          if (r.outcome_event_id && attributedBookings?.[r.outcome_event_id]) {
            revenue += attributedBookings[r.outcome_event_id].price;
          }
        }
      }
    }
    return {
      sent,
      responded,
      converted,
      revenue,
      response_rate: sent > 0 ? Math.round((responded / sent) * 100) : 0,
      conversion_rate: sent > 0 ? Math.round((converted / sent) * 100) : 0,
    };
  }, [campaigns, attributedBookings]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Page Header & Description ── */}
      <div className="flex flex-row items-start justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold text-foreground">Campaigns</h2>
          <p className="text-sm text-muted-foreground">Monitor and configure automated winback campaigns</p>
        </div>
      </div>

      {/* ── Mode toggle + graduation prompt ── */}
      <div className="border border-border rounded-xl bg-card p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
        <div>
          <p className="text-sm font-medium text-foreground">
            Auto-send winback batches
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {winbackMode === "auto_send"
              ? "Daily batches send automatically. Disable to review each batch first."
              : "Daily batches wait in Actions for your approval before sending."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {eligibleForGraduation && winbackMode === "review_first" && (
            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1">
              ✓ 3 batches approved — ready to auto-send?
            </span>
          )}
          <Switch
            checked={winbackMode === "auto_send"}
            disabled={pendingMode !== null}
            onCheckedChange={(checked) => handleModeChange(checked ? "auto_send" : "review_first")}
          />
        </div>
      </div>

      {/* ── Include-images toggle ── */}
      <div className="border border-border rounded-xl bg-card p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
        <div>
          <p className="text-sm font-medium text-foreground">
            Include images in winback messages
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {includeImages
              ? "Email & Telegram recipients get a relevant listing photo as a header image. You can still turn it off per batch."
              : "Messages are text-only. Turn on to lead with a relevant listing photo."}
          </p>
        </div>
        <Switch
          checked={includeImages}
          disabled={pendingImages}
          onCheckedChange={handleImagesChange}
        />
      </div>

      {/* ── KPI strip ── */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Last 30 days</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPIBlock
            label="Winbacks sent"
            value={kpis.sent.toString()}
            icon={<Send className="h-4 w-4" />}
          />
          <KPIBlock
            label="Response rate"
            value={`${kpis.response_rate}%`}
            sub={`${kpis.responded} / ${kpis.sent || 0}`}
            icon={<MessageSquare className="h-4 w-4" />}
          />
          <KPIBlock
            label="Conversion rate"
            value={`${kpis.conversion_rate}%`}
            sub={`${kpis.converted} converted`}
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPIBlock
            label="Attributed revenue"
            value={`$${kpis.revenue.toFixed(2)}`}
            sub={kpis.converted > 0 ? `across ${kpis.converted} booking${kpis.converted === 1 ? "" : "s"}` : "—"}
            icon={<DollarSign className="h-4 w-4" />}
            highlight
          />
        </div>
        {learningInsight && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
            <TrendingUp className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">{learningInsight}</p>
          </div>
        )}
      </div>

      {/* ── Campaign List Table ── */}
      <div className="space-y-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-foreground">All campaigns</p>
          <p className="text-sm text-muted-foreground mt-1">Each row is one batch. Click to see recipients + outcomes.</p>
        </div>

        <div className="rounded-lg border border-border overflow-hidden bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent [&>th]:bg-background">
                <TableHead className="py-3 px-5">Campaign</TableHead>
                <TableHead className="text-right py-3 px-5">Sent</TableHead>
                <TableHead className="text-right py-3 px-5">Response</TableHead>
                <TableHead className="text-right py-3 px-5">Conversion</TableHead>
                <TableHead className="text-right py-3 px-5">Revenue</TableHead>
                <TableHead className="w-[50px] py-3 px-5"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center py-6">
                      <div className="inline-flex rounded-full bg-muted border border-border p-3 mb-2">
                        <Send className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground font-medium">No winback campaigns yet.</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Daily cron sends to dormant contacts, or use Send Winback on a customer profile.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((c) => {
                  const revenue = c.recipients.reduce((acc, r) => {
                    if (r.outcome === "converted" && r.outcome_event_id && attributedBookings?.[r.outcome_event_id]) {
                      return acc + attributedBookings[r.outcome_event_id].price;
                    }
                    return acc;
                  }, 0);
                  const respRate = c.sent_count > 0 ? Math.round((c.responded_count / c.sent_count) * 100) : 0;
                  const convRate = c.sent_count > 0 ? Math.round((c.converted_count / c.sent_count) * 100) : 0;

                  return (
                    <TableRow
                      key={c.campaign_id}
                      onClick={() => setSelectedCampaign(c)}
                      className="group cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <TableCell className="py-3.5 px-5 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground truncate">
                            {formatCampaignLabel(c.campaign_id)}
                          </span>
                          {c.trigger_source === "manual_admin" && (
                            <Badge variant="outline" className="text-xs py-0.5 px-2 bg-muted/30 font-medium">Manual</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          {format(new Date(c.first_sent_at), "MMM d, yyyy 'at' h:mm a")} · {safeDistance(c.first_sent_at)}
                        </p>
                      </TableCell>
                      <TableCell className="text-right py-3.5 px-5 text-sm font-medium text-foreground">
                        {c.sent_count}
                      </TableCell>
                      <TableCell className="text-right py-3.5 px-5">
                        <span className="text-sm font-medium text-foreground">{respRate}%</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{c.responded_count} replies</p>
                      </TableCell>
                      <TableCell className="text-right py-3.5 px-5">
                        <span className="text-sm font-medium text-foreground">{convRate}%</span>
                        <p className="text-xs text-muted-foreground mt-0.5">{c.converted_count} converted</p>
                      </TableCell>
                      <TableCell className="text-right py-3.5 px-5 font-medium">
                        <span className={cn(revenue > 0 ? "text-emerald-600 font-semibold" : "text-muted-foreground")}>
                          {revenue > 0 ? `$${revenue.toFixed(2)}` : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-3.5 px-5 text-right w-[50px]">
                        <ChevronRight className="h-4 w-4 text-muted-foreground inline-block group-hover:translate-x-0.5 transition-transform" />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {campaigns.length > 0 && (
          <div className="flex justify-end mt-4">
            <p className="text-[12px] text-muted-foreground tracking-wider font-medium">
              Showing {campaigns.length} of campaign batch(es)
            </p>
          </div>
        )}
      </div>

      <CampaignDetailModal
        open={!!selectedCampaign}
        onOpenChange={(open) => !open && setSelectedCampaign(null)}
        campaign={selectedCampaign}
        attributedBookings={attributedBookings ?? {}}
      />
    </div>
  );
};

// KPI block

const KPIBlock = ({
  label,
  value,
  sub,
  icon,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
  highlight?: boolean;
}) => (
  <div
    className={cn(
      "rounded-lg border p-5 transition-all shadow-sm",
      highlight
        ? "border-emerald-500/20 bg-emerald-50/30"
        : "border-border bg-card",
    )}
  >
    <div className="flex items-center justify-between mb-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <span className={cn(highlight ? "text-emerald-500" : "text-muted-foreground/70")}>{icon}</span>
    </div>
    <p className={cn("text-xl font-semibold font-heading", highlight ? "text-emerald-600" : "text-foreground")}>
      {value}
    </p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

// Recipient drill-down modal

interface CampaignDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: CampaignAgg | null;
  attributedBookings: Record<string, { booking_id: string; price: number; name?: string; short_id?: string; created_at?: string }>;
}

const CampaignDetailModal = ({ open, onOpenChange, campaign, attributedBookings }: CampaignDetailModalProps) => {
  const contactIds = campaign?.recipients.map((r) => r.contact_id) ?? [];
  const { data: contactNames } = useQuery({
    queryKey: ["recipient_names", contactIds.sort().join(",")],
    enabled: contactIds.length > 0,
    queryFn: async (): Promise<Record<string, { name: string | null; customer_id: string | null }>> => {
      const { data: idents } = await (supabase as any)
        .from("channel_identifiers")
        .select("contact_id, identifier_value, channel_type")
        .in("contact_id", contactIds);
      const rows = (idents ?? []) as Array<{ contact_id: string; identifier_value: string; channel_type: string }>;
      const emailByContact = new Map<string, string>();
      const phoneByContact = new Map<string, string>();
      for (const r of rows) {
        if (r.channel_type === "email" && !emailByContact.has(r.contact_id)) emailByContact.set(r.contact_id, r.identifier_value);
        if (r.channel_type === "whatsapp_e164" && !phoneByContact.has(r.contact_id)) phoneByContact.set(r.contact_id, r.identifier_value);
      }
      const emails = Array.from(new Set(emailByContact.values()));
      const phones = Array.from(new Set(phoneByContact.values()));
      const orParts: string[] = [];
      if (emails.length) orParts.push(`email.in.(${emails.join(",")})`);
      if (phones.length) orParts.push(`phone.in.(${phones.join(",")})`);
      if (orParts.length === 0) return {};
      const { data: customers } = await (supabase as any)
        .from("customers")
        .select("customer_id, name, email, phone")
        .or(orParts.join(","));
      const out: Record<string, { name: string | null; customer_id: string | null }> = {};
      for (const cid of contactIds) {
        const email = emailByContact.get(cid);
        const phone = phoneByContact.get(cid);
        const match = (customers ?? []).find(
          (c: any) => (email && c.email === email) || (phone && c.phone === phone),
        );
        out[cid] = {
          name: match?.name ?? null,
          customer_id: match?.customer_id ?? null,
        };
      }
      return out;
    },
  });

  if (!campaign) return null;
  const totalRevenue = campaign.recipients.reduce((acc, r) => {
    if (r.outcome === "converted" && r.outcome_event_id && attributedBookings[r.outcome_event_id]) {
      return acc + attributedBookings[r.outcome_event_id].price;
    }
    return acc;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
        <DialogHeader className="pb-3 border-b border-border">
          <DialogTitle className="text-base font-semibold flex items-center gap-2 font-heading">
            {formatCampaignLabel(campaign.campaign_id)}
            {campaign.trigger_source === "manual_admin" && (
              <Badge variant="outline" className="text-xs py-0.5 px-2 font-medium">Manual</Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {format(new Date(campaign.first_sent_at), "MMM d, yyyy 'at' h:mm a")} · {safeDistance(campaign.first_sent_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 py-4">
          <MiniStat label="Sent" value={campaign.sent_count} />
          <MiniStat label="Responded" value={campaign.responded_count} />
          <MiniStat label="Converted" value={campaign.converted_count} />
          <MiniStat label="Revenue" value={`$${totalRevenue.toFixed(2)}`} highlight={totalRevenue > 0} />
        </div>

        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-heading">
            Recipients
          </h3>
          <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
            {campaign.recipients.map((r) => {
              const name = contactNames?.[r.contact_id]?.name ?? "—";
              const customerId = contactNames?.[r.contact_id]?.customer_id ?? null;
              const booking = r.outcome_event_id ? attributedBookings[r.outcome_event_id] : null;
              const outcomeBadge =
                r.outcome === "converted"
                  ? { bg: "bg-emerald-50 text-emerald-700 border-emerald-200/50", icon: <CheckCircle2 className="h-3 w-3" />, label: r.outcome_type === "booking_created" ? "Booked" : r.outcome_type === "payment_succeeded" ? "Paid" : "Converted" }
                  : r.outcome === "responded"
                    ? { bg: "bg-blue-50 text-blue-700 border-blue-200/50", icon: <MessageSquare className="h-3 w-3" />, label: "Responded" }
                    : r.outcome === "ignored"
                      ? { bg: "bg-zinc-100 text-zinc-600 border-zinc-200/50", icon: <XCircle className="h-3 w-3" />, label: "Ignored" }
                      : { bg: "bg-amber-50 text-amber-700 border-amber-200/50", icon: <Clock className="h-3 w-3" />, label: "Awaiting" };
              return (
                <div key={r.contact_id} className="px-4 py-3 flex items-start justify-between gap-3 hover:bg-muted/50 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {customerId ? (
                        <a
                          href={`?tab=customer-details&id=${customerId}`}
                          className="text-sm font-medium text-foreground hover:underline truncate"
                        >
                          {name}
                        </a>
                      ) : (
                        <span className="text-sm font-medium text-foreground truncate">{name}</span>
                      )}
                      {r.channel && (
                        <Badge variant="outline" className="capitalize text-[10px] py-0 px-1.5 h-4">{r.channel}</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Sent {safeDistance(r.sent_at)}
                      {r.outcome_at && r.outcome !== "sent" && (
                        <> · {r.outcome === "responded" ? "replied" : r.outcome === "ignored" ? "ignored" : "converted"} {safeDistance(r.outcome_at)}</>
                      )}
                    </p>
                    {booking && (
                      <p className="text-[11px] text-emerald-700 mt-1">
                        → {booking.name ?? "Booking"} · ${booking.price.toFixed(2)} ({booking.short_id ?? booking.booking_id.slice(0, 8)})
                      </p>
                    )}
                  </div>
                  <div className={cn("shrink-0 inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2.5 py-0.5", outcomeBadge.bg)}>
                    {outcomeBadge.icon}
                    {outcomeBadge.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MiniStat = ({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) => (
  <div className={cn(
    "rounded-lg border p-4 shadow-sm",
    highlight
      ? "border-emerald-500/20 bg-emerald-50/30"
      : "border-border bg-card",
  )}>
    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
    <p className={cn("text-lg font-semibold font-heading", highlight ? "text-emerald-600" : "text-foreground")}>{value}</p>
  </div>
);

export default CampaignsAdmin;
